import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Theme, createTheme, responsiveFontSizes } from "@mui/material/styles";
import { PaletteOptions } from "@mui/material/styles/createPalette";

import "./fonts.css";

export const customPalette = {
  mode: "light",
  primary: {
    main: "#0090C5",
    light: "#3572A6",
    dark: "#023a67",
    contrastText: "#fff",
  },
  secondary: {
    main: "rgba(0, 0, 0, 0.6)",
    light: "#EFEFEF",
    dark: "#393939",
  },
  grey: {
    100: "#F5F5F5",
    300: "#D9DADA",
    500: "#767676",
    600: "#646464",
    800: "#44474A",
    900: "#333333",
  },
} satisfies PaletteOptions;

const customTheme = createTheme({
  palette: customPalette,
  colorSchemes: {
    dark: false,
  },
  typography: {
    fontFamily: "Roboto, sans-serif",
    h1: {
      fontSize: "2rem",
      color: customPalette.primary.main,
    },
    h2: {
      fontSize: "1.75rem",
      color: customPalette.primary.main,
    },
    h3: {
      fontSize: "1.5rem",
    },
    h4: {
      fontSize: "1.25rem",
    },
    h5: {
      fontSize: "1.1rem",
    },
  },
  shape: {},
  components: {
    MuiBackdrop: {
      styleOverrides: {
        root: {
          zIndex: "3",
        },
      },
    },
    MuiCssBaseline: {
      //eslint-disable-next-line
      styleOverrides: (themeParam) => ({
        body: {
          minHeight: "100vh",
        },
        "#root": {
          minHeight: "100vh",
          ">div.MuiBox-root:nth-of-type(2)": {
            minHeight: "100vh",
          },
        },
      }),
    },
    MuiButtonBase: {
      defaultProps: {
        // disableRipple: true,
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: "none",
          borderRadius: 0,
          position: "relative",
          outline: "1px solid " + customPalette.primary.main,
          "&:hover": {
            backgroundColor: "#fff",
            outlineWidth: "2px",
          },
          "&.MuiPaper-elevation8": {
            outlineWidth: "3px",
          },
        },
      },
    },
    MuiCardMedia: {
      styleOverrides: {
        root: {
          objectFit: "cover",
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: customPalette.primary.main,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          textTransform: "none",
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: ".9rem",
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          textTransform: "none",
          "&:hover>.MuiOutlinedInput-notchedOutline": {
            borderColor: customPalette.primary.main,
            borderWidth: "2px",
          },
        },
        notchedOutline: {
          borderColor: customPalette.primary.main,
          borderWidth: "1px",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 0,
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          boxShadow: "none",
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: "8px",
        },
        head: {
          fontWeight: "400 !important",
        },
        body: { fontWeight: "bold" },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          border: "1px solid " + customPalette.secondary.light,
          background: "#fff",
          "&::before": {
            top: 0,
            height: "100%",
            border: "none",
            borderColor: customPalette.primary.main,
          },

          "&:hover": {
            background: "#fff",
            "&::before": {
              border: "2px solid " + customPalette.primary.main + " !important",
            },
          },
        },
        input: {
          paddingTop: "8px",
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          "&.Mui-focused": {
            background: "#fff",
            "&::before": {
              border: "2px solid " + customPalette.primary.dark + " !important",
            },
          },
        },
        icon: {
          // Add style overrides for the icon (optional)
          color: customPalette.primary.main, // Example of color change
        },
      },
      defaultProps: {
        IconComponent: ExpandMoreIcon,
      },
    },
    MuiGrid2: {
      styleOverrides: {
        root: {
          ".MuiCard-root": {
            height: "100%",
          },
          ".renderWithBorder": {
            border: "1px solid " + customPalette.primary.main,
            borderRadius: "4px",
            padding: "1rem",
          },
          "&.renderAsSwitch": {
            ".MuiCard-root": {
              "&.MuiPaper-elevation8": {
                backgroundColor: customPalette.primary.main,
                color: customPalette.primary.contrastText,
                outlineWidth: "2px",
              },
              "&:hover": {
                outlineWidth: "2px",
              },
            },
            ".MuiCardContent-root": {
              paddingBlock: "0.25rem",
              textAlign: "center",
            },
          },
          ".ValueContent-image": {
            display: "flex",
            flexGrow: "1",
            justifyContent: "center",
          },
          "&.renderAsCustomThumbnail": {
            ".MuiCard-root": {
              outlineWidth: "0px",
              "&.MuiPaper-elevation8": {
                outlineWidth: "2px",
              },
              "&:hover": {
                outlineWidth: "2px",
              },
              ".ValueContent-value": {
                color: customPalette.primary.main,
                fontSize: "1.25rem",
                fontWeight: "bold",
              },
              ".ValueContent-image": {
                // width: "initial",
              },
            },
          },
          ".configuration-headline": {
            borderBottom: "1px solid " + customPalette.secondary.light,
            paddingBottom: "0.5rem",
            marginBottom: "0.5rem",
          },
          "&.Other-CadClickCadBox": {
            overflow: "auto",
          },
          ">.attribute-wrapper": (
            theme: Theme,
            //theme: Omit<Theme, "components" | "palette"> & CssVarsTheme,
          ) => ({
            [theme.breakpoints.down("md")]: {
              ">div.MuiGrid2-root": { flexDirection: "column" },
            },
          }),
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        list: {
          gap: "1rem",
        },
        indicator: {
          display: "none",
        },
        scroller: {
          overflow: "auto !important",
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          border: "1px solid " + customPalette.secondary.light,
          borderRadius: "8px",
          "&.Mui-selected": {
            background: customPalette.primary.main,
            color: customPalette.primary.contrastText,
            border: "1px solid " + customPalette.primary.main,
          },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          color: customPalette.primary.main,
        },
      },
    },
  },
});

export const theme = responsiveFontSizes(customTheme);
